import React, { useState, useEffect } from 'react';
import { Loader2, PlusCircle, FileText, ExternalLink, AlertCircle, X, Video, Edit2, ChevronDown, ChevronUp } from 'lucide-react';

const RiversideVideos = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const [formData, setFormData] = useState({
    title: '',
    gdrive_link: '',
    show_notes: '',
    videos: []
  });

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/riverside/videos`);
      const data = await response.json();
      if (data.success) {
        setEntries(data.entries || []);
      } else {
        setError('Failed to load riverside videos');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load riverside videos');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVideoChange = (index, field, value) => {
    setFormData(prev => {
      const videos = [...prev.videos];
      videos[index] = {
        ...videos[index],
        [field]: value
      };
      return {
        ...prev,
        videos
      };
    });
  };

  const addVideo = () => {
    setFormData(prev => ({
      ...prev,
      videos: [...prev.videos, { title: '', link: '', type: 'Full', published_to_yt: false }]
    }));
  };

  const removeVideo = (index) => {
    setFormData(prev => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.gdrive_link) {
      alert('Please enter both a main title and Riverside Exports link.');
      return;
    }

    try {
      setSubmitting(true);
      const url = editingId 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/riverside/videos/${editingId}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/riverside/videos`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        setFormData({ title: '', gdrive_link: '', show_notes: '', videos: [] });
        setEditingId(null);
        fetchEntries();
      } else {
        alert(data.message || 'Failed to save entry');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to save entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (entry) => {
    setFormData({
      title: entry.title || '',
      gdrive_link: entry.gdrive_link || '',
      show_notes: entry.show_notes || '',
      videos: entry.videos || []
    });
    setEditingId(entry.id);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setFormData({ title: '', gdrive_link: '', show_notes: '', videos: [] });
    setEditingId(null);
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Riverside Video Uploads</h1>
        <p className="mt-2 text-gray-600">
          Track Riverside video requests with show notes and Google Drive folders.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-purple-600 font-semibold">
            <PlusCircle className="w-5 h-5" />
            <span>{editingId ? 'Edit Riverside Upload' : 'New Riverside Upload'}</span>
          </div>
          {editingId && (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Main Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Riverside episode title"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Riverside Exports Link</label>
            <input
              type="url"
              name="gdrive_link"
              value={formData.gdrive_link}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="https://drive.google.com/..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Show Notes</label>
            <textarea
              name="show_notes"
              value={formData.show_notes}
              onChange={handleChange}
              rows={6}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Detailed show outline, talking points, CTA, etc."
            />
          </div>
        </div>

        {/* Video Links Section */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center space-x-2 text-purple-600 font-semibold mb-4">
            <Video className="w-5 h-5" />
            <span>Video Links</span>
          </div>

          {formData.videos.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No videos added yet. Click "Add Video" to add one.</p>
          ) : (
            <div className="space-y-3">
              {formData.videos.map((video, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">Video {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeVideo(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                      <input
                        type="text"
                        value={video.title}
                        onChange={(e) => handleVideoChange(index, 'title', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Video title"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Link</label>
                      <input
                        type="url"
                        value={video.link}
                        onChange={(e) => handleVideoChange(index, 'link', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="https://..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                      <select
                        value={video.type}
                        onChange={(e) => handleVideoChange(index, 'type', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="Full">Full</option>
                        <option value="Segment">Segment</option>
                        <option value="Short">Short</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Published to YT</label>
                      <select
                        value={video.published_to_yt ? 'Yes' : 'No'}
                        onChange={(e) => handleVideoChange(index, 'published_to_yt', e.target.value === 'Yes')}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={addVideo}
            className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 flex items-center space-x-2"
          >
            <PlusCircle className="w-4 h-4" />
            <span>Add Video</span>
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FileText className="w-4 h-4" />
                <span>Save Entry</span>
              </>
            )}
          </button>
        </div>
      </form>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Saved Entries</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center space-x-2 text-red-600 px-6 py-4">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No riverside uploads yet.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {entries.map(entry => {
              const isExpanded = expandedIds.has(entry.id);
              return (
                <div key={entry.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 flex-1">
                      <button
                        onClick={() => toggleExpand(entry.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </button>
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900">{entry.title}</h3>
                        <p className="text-xs text-gray-500">
                          Created {new Date(entry.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleEdit(entry)}
                        className="inline-flex items-center text-sm text-purple-600 hover:text-purple-800"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        <span>Edit</span>
                      </button>
                      {entry.gdrive_link && (
                        <a
                          href={entry.gdrive_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-purple-600 hover:text-purple-800"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          View Folder
                        </a>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="mt-3 pl-7">
                      {entry.show_notes && (
                        <div className="mb-3 bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                          {entry.show_notes}
                        </div>
                      )}
                      {entry.videos && entry.videos.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Videos:</h4>
                          <div className="space-y-2">
                            {entry.videos.map((video, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-2 text-sm">
                                <div className="flex items-center space-x-3">
                                  <span className="font-medium text-gray-900">{video.title}</span>
                                  <span className="text-gray-500">({video.type})</span>
                                  {video.published_to_yt && (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                      YT Published
                                    </span>
                                  )}
                                </div>
                                {video.link && (
                                  <a
                                    href={video.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-purple-600 hover:text-purple-800"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {(!entry.show_notes && (!entry.videos || entry.videos.length === 0)) && (
                        <p className="text-sm text-gray-500 italic">No additional details</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RiversideVideos;




